// action - state management
import { LOGIN, LOGOUT, REGISTER, UPDATE_USER } from './accountActions';
import type { CustomerFeatures } from 'contracts/customer';

export interface NexviaUser {
  id: string;
  email: string | null;
  avatar: string | null;
  name: string;
  firstName: string | null;
  lastName: string | null;
  avatarVersion: number | null;
  phone: string | null;
  // Client phone verification: true only when the API holds `phone` as proven by an SMS code on
  // this client's own Firebase account (`clients."phoneVerifiedAt"`). Having a phone never counts.
  phoneVerified: boolean;
  pilotEnabled: boolean;
  features: CustomerFeatures;
}

export interface AccountState {
  isLoggedIn: boolean;
  isInitialized: boolean;
  user: NexviaUser | null;
  // True from the moment a signed-in user went away, whether this tab logged out, another tab did, or
  // Firebase dropped the session, until the next sign-in. A visitor who was never signed in does not
  // have it. AuthGuard reads it: the page someone was on when their session ended is not somewhere to
  // come back to (NEX-1585).
  sessionEnded: boolean;
}

export type AccountAction =
  | { type: typeof REGISTER; payload: { user: NexviaUser } }
  | { type: typeof LOGIN; payload: { isLoggedIn: boolean; user: NexviaUser } }
  | { type: typeof UPDATE_USER; payload: Partial<NexviaUser> }
  | { type: typeof LOGOUT };

const initialState: AccountState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  sessionEnded: false
};

export default function accountReducer(state: AccountState = initialState, action: AccountAction): AccountState {
  switch (action.type) {
    case REGISTER: {
      const { user } = action.payload;
      return {
        ...state,
        user
      };
    }
    case LOGIN: {
      const { user } = action.payload;
      return {
        ...state,
        isLoggedIn: true,
        isInitialized: true,
        sessionEnded: false,
        user
      };
    }
    case UPDATE_USER: {
      // Merges into the user already in state, never into a stale copy of it. A no-op once logged
      // out, so an answer that lands after a logout cannot bring the user back.
      if (!state.user) return state;
      return { ...state, user: { ...state.user, ...action.payload } };
    }
    case LOGOUT: {
      return {
        ...state,
        isInitialized: true,
        isLoggedIn: false,
        // A LOGOUT with nobody signed in is the listener's first answer for a visitor, not an end; one
        // that follows a sign-in is, and stays so if Firebase repeats it.
        sessionEnded: state.isLoggedIn || state.sessionEnded,
        user: null
      };
    }
    default: {
      return { ...state };
    }
  }
}
